import React, { useState } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import { CONTROL_INPUT, MUTED_LABEL, FREQUENCY_OPTIONS, PAGE_OPTIONS } from './bug-trap-ui.js';
import { apiFetch } from '../../lib/api-client.js';

const EMPTY_FORM = { what_doing: '', what_happened: '', which_page: '', frequency: '', free_text: '' };

interface IssueSubmitResponse {
  id?: string;
  error?: string;
}

export default function BugTrapIntakeForm({ onSubmitted }: { onSubmitted?: () => void }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  function set(field: keyof typeof EMPTY_FORM, value: string) { setForm((prev) => ({ ...prev, [field]: value })); }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    if (!form.what_doing.trim()) { setError('Required: What were you doing?'); return; }
    if (!form.what_happened.trim()) { setError('Required: What happened?'); return; }
    if (!form.which_page) { setError('Required: Which page?'); return; }
    if (!form.frequency) { setError('Required: How often?'); return; }

    setSubmitting(true);
    try {
      const result = await apiFetch<IssueSubmitResponse>('/api/portal-issues', {
        method: 'POST',
        body: JSON.stringify({
          what_doing: form.what_doing.trim(),
          what_happened: form.what_happened.trim(),
          which_page: form.which_page,
          frequency: form.frequency,
          free_text: form.free_text.trim() || null,
        }),
      });

      if (result.error) { setError(result.error); return; }

      setSuccess(`Report submitted. ID: ${result.id}`);
      setForm(EMPTY_FORM);
      if (onSubmitted) onSubmitted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={LAYOUT.subPanel}>
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 12 }}>
        Report a bug
      </div>

      {success && <div style={{ color: C.green, fontSize: 13, marginBottom: 12 }}>{success}</div>}
      {error && <div style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>{error}</div>}

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gap: 14 }}>
          <div>
            <label style={MUTED_LABEL}>What were you trying to do? *</label>
            <input type="text" value={form.what_doing} onChange={(e) => set('what_doing', e.target.value)} style={CONTROL_INPUT} placeholder="e.g. clock in a driver" />
          </div>
          <div>
            <label style={MUTED_LABEL}>What happened? *</label>
            <textarea value={form.what_happened} onChange={(e) => set('what_happened', e.target.value)} rows={3} style={{ ...CONTROL_INPUT, resize: 'vertical' }} placeholder="Describe what went wrong" />
          </div>
          <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
            <div>
              <label style={MUTED_LABEL}>Which page? *</label>
              <select value={form.which_page} onChange={(e) => set('which_page', e.target.value)} style={CONTROL_INPUT}>
                <option value="">Select a page</option>
                {PAGE_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label style={MUTED_LABEL}>How often? *</label>
              <select value={form.frequency} onChange={(e) => set('frequency', e.target.value)} style={CONTROL_INPUT}>
                <option value="">Select frequency</option>
                {FREQUENCY_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={MUTED_LABEL}>Anything else?</label>
            <textarea value={form.free_text} onChange={(e) => set('free_text', e.target.value)} rows={2} style={{ ...CONTROL_INPUT, resize: 'vertical' }} placeholder="Optional context, screenshots link, etc." />
          </div>
          <div>
            <button type="submit" disabled={submitting} style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: C.gold, color: '#111', fontWeight: 700, cursor: submitting ? 'wait' : 'pointer', fontSize: 14 }}>
              {submitting ? 'Submitting...' : 'Submit bug report'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
